import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, ReplaySubject, takeUntil } from 'rxjs';

@Component({
  selector: 'header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss', './header.component.mobile.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  readonly scrollOffsetToShowHeader = 50;

  isMobile: boolean;
  currentPageYOffset: number = 0;

  private readonly _isVisible$ = new BehaviorSubject<boolean>(true);
  private readonly _navBarExpanded$ = new BehaviorSubject<boolean>(false);
  private readonly _vehiclesMenuOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _humanSpaceflightMenuOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _xAIMenuOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _shopMenuOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _companyMenuOpen$ = new BehaviorSubject<boolean>(false);
  readonly isVisible$: Observable<boolean> = this._isVisible$.asObservable().pipe(distinctUntilChanged());
  readonly navBarExpanded$: Observable<boolean> = this._navBarExpanded$.asObservable().pipe(distinctUntilChanged());
  readonly vehiclesMenuOpen$: Observable<boolean> = this._vehiclesMenuOpen$.asObservable().pipe(distinctUntilChanged());
  readonly humanSpaceflightMenuOpen$: Observable<boolean> = this._humanSpaceflightMenuOpen$.asObservable().pipe(distinctUntilChanged());
  readonly xAIMenuOpen$: Observable<boolean> = this._xAIMenuOpen$.asObservable().pipe(distinctUntilChanged());
  readonly shopMenuOpen$: Observable<boolean> = this._shopMenuOpen$.asObservable().pipe(distinctUntilChanged());
  readonly companyMenuOpen$: Observable<boolean> = this._companyMenuOpen$.asObservable().pipe(distinctUntilChanged());

  readonly anyMenuOpen$: Observable<boolean> = combineLatest([
    this.vehiclesMenuOpen$,
    this.humanSpaceflightMenuOpen$,
    this.xAIMenuOpen$,
    this.shopMenuOpen$,
    this.companyMenuOpen$,
  ]).pipe(
    map((states) => states.some(Boolean)),
    distinctUntilChanged(),
  );

  private readonly _destroy$ = new ReplaySubject<void>(1);

  constructor(readonly breakpointObserver: BreakpointObserver, private router: Router, @Inject('contentHubEnabled') public contentHubEnabled: string | boolean) { }

  ngOnInit(): void {
    // Enter mobile mode at an earlier breakpoint to handle mini-desktop / tablets.
    this.breakpointObserver.observe([Breakpoints.Medium, Breakpoints.Small, Breakpoints.XSmall]).subscribe(
      (state: BreakpointState) => this.isMobile = state.matches);

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntil(this._destroy$)
    ).subscribe((event: NavigationEnd) => {
      if (this._navBarExpanded$.value) {
        this._navBarExpanded$.next(false);
        this._vehiclesMenuOpen$.next(false);
        this._humanSpaceflightMenuOpen$.next(false);
        this._xAIMenuOpen$.next(false);
        this._shopMenuOpen$.next(false);
        this._companyMenuOpen$.next(false);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    const pageYOffset = window.pageYOffset;

    // Show the header if scrolling upwards, if we're still at the top of the page, or if the mobile navbar is expanded.
    if (pageYOffset < this.currentPageYOffset ||
      pageYOffset < this.scrollOffsetToShowHeader ||
      this._navBarExpanded$.value) {
      this._isVisible$.next(true);
    } else {
      this._isVisible$.next(false);
    }

    this.currentPageYOffset = pageYOffset;
  }

  toggleNavBar() {
    this._navBarExpanded$.next(!this._navBarExpanded$.value);
  }

  toggleVehiclesMenu() {
    this._vehiclesMenuOpen$.next(!this._vehiclesMenuOpen$.value);
  }

  openVehiclesMenu() {
    if (!this.isMobile) {
      this.closeAllMenusExcept('vehicles');
      this._vehiclesMenuOpen$.next(true);
    }
  }

  closeVehiclesMenu() {
    if (!this.isMobile) {
      this._vehiclesMenuOpen$.next(false);
    }
  }

  toggleHumanSpaceflightMenu() {
    this._humanSpaceflightMenuOpen$.next(!this._humanSpaceflightMenuOpen$.value);
  }

  openHumanSpaceflightMenu() {
    if (!this.isMobile) {
      this.closeAllMenusExcept('humanSpaceflight');
      this._humanSpaceflightMenuOpen$.next(true);
    }
  }

  closeHumanSpaceflightMenu() {
    if (!this.isMobile) {
      this._humanSpaceflightMenuOpen$.next(false);
    }
  }

  togglexAIMenu() {
    this._xAIMenuOpen$.next(!this._xAIMenuOpen$.value);
  }

  openxAIMenu() {
    if (!this.isMobile) {
      this.closeAllMenusExcept('xAI');
      this._xAIMenuOpen$.next(true);
    }
  }

  closexAIMenu() {
    if (!this.isMobile) {
      this._xAIMenuOpen$.next(false);
    }
  }

  toggleShopMenu() {
    this._shopMenuOpen$.next(!this._shopMenuOpen$.value);
  }

  openShopMenu() {
    if (!this.isMobile) {
      this.closeAllMenusExcept('shop');
      this._shopMenuOpen$.next(true);
    }
  }

  closeShopMenu() {
    if (!this.isMobile) {
      this._shopMenuOpen$.next(false);
    }
  }

  toggleCompanyMenu() {
    this._companyMenuOpen$.next(!this._companyMenuOpen$.value);
  }

  openCompanyMenu() {
    if (!this.isMobile) {
      this.closeAllMenusExcept('company');
      this._companyMenuOpen$.next(true);
    }
  }

  closeCompanyMenu() {
    if (!this.isMobile) {
      this._companyMenuOpen$.next(false);
    }
  }

  isContentHubEnabled(): boolean {
    return this.contentHubEnabled === "true" || this.contentHubEnabled === true;
  }

  /** Close every desktop submenu except the one currently being opened. */
  private closeAllMenusExcept(except?: 'vehicles' | 'humanSpaceflight' | 'xAI' | 'shop' | 'company'): void {
    if (except !== 'vehicles') {
      this._vehiclesMenuOpen$.next(false);
    }
    if (except !== 'humanSpaceflight') {
      this._humanSpaceflightMenuOpen$.next(false);
    }
    if (except !== 'xAI') {
      this._xAIMenuOpen$.next(false);
    }
    if (except !== 'shop') {
      this._shopMenuOpen$.next(false);
    }
    if (except !== 'company') {
      this._companyMenuOpen$.next(false);
    }
  }
}
